// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductActions, { ProductActionsStyle } from "@adl/App/Components/Product/ProductActions";
import { REORDER_PRODUCTS_ID } from "@adl/App/Widgets/ReorderPad/ReorderPad";
import ProductPartNumbers from "@adl/Overrides/Components/ProductPartNumbers";
import { EMPTY_VALUE } from "@adl/XDL/constants";
import { color, ellipsis, mergeWithCss, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    extendedStyles?: ReorderPadSummaryCardStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, REORDER_PRODUCTS_ID, ownProps.product.id),
    settingsCollection: getSettingsCollection(state),
    session: getSession(state),
});

const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadSummaryCardStyles {
    dataCell?: DataTableCellProps;
    descriptionCell?: DataTableCellProps;
    descriptionLink?: LinkPresentationProps;
    productPrice?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    actionsWrapper?: InjectableCss;
    addToCartSection?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productActionStyles?: ProductActionsStyle;
    compareWrapper?: TypographyPresentationProps;
}

export const reorderPadSummaryCardStyles: ReorderPadSummaryCardStyles = {
    descriptionCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    dataCell: {
        css: css`
            & > ${StyledButton}, & > ${StyledButton} > ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 24px;
                font-weight: normal;
                color: ${color("text.main")};
                &:hover {
                    color: ${color("common.ui1")};
                }
            }
            & > div[data-test-selector="productListProductImage"] {
                max-width: 110px;
                max-height: 110px;
            }
        `,
    },
    descriptionLink: {
        typographyProps: {
            css: css`
                ${ellipsis(2)}
            `,
        },
    },
    productPrice: {
        price: {
            priceText: {
                size: 15,
                weight: "normal",
                css: css`
                    line-height: 24px;
                `,
            },
        },
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    addToCartSection: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            css: css`
                align-self: baseline;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    max-width: 20ch;
                    ${ellipsis(5)}
                `,
            },
        },
        productDetailDescription: {
            css: css`
                max-width: 20ch;
                ${ellipsis(5)}
            `,
        },
    },
    productActionStyles: {
        productQuantityOrderedStyles: {
            actionsStyles: {
                actionClickable: {
                    css: css`
                        padding: 0 3px;
                    `,
                },
            },
        },
    },
    compareWrapper: {
        css: css`
            display: flex;
            [data-id="checkbox"] {
                padding: 0;
                margin-right: 50px;
            }
        `,
    },
};

const ReorderPadSummaryCard: FC<Props> = ({
    product,
    productInfo,
    settingsCollection,
    session,
    updateProductInfo,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(reorderPadSummaryCardStyles, extendedStyles));
    if (!product || !productInfo) {
        return null;
    }

    const data = {
        id: product.id,
        productDescription: product.productTitle,
        lastOrderedQty: product.properties.lastOrderedQty,
        lastOrdered: product.properties.lastOrderedDate
            ? getLocalizedDateTime({
                  dateTime: new Date(product.properties.lastOrderedDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        pricing: `$0.00`,
        inventory: `$0.00`,
        brand: product.brand,
        manufacturerItem: product.manufacturerItem,
        productNumber: product.productNumber,
    };

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: REORDER_PRODUCTS_ID, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    return (
        <ProductContext.Provider value={productContext}>
            <DataTableCell {...styles.dataCell}>
                <ProductListProductImage showImage={true} showCompare={false} />
            </DataTableCell>
            <DataTableCell {...styles.descriptionCell}>
                <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                <ProductPartNumbers
                    manufacturerItem={data.manufacturerItem}
                    productNumber={data.productNumber}
                    {...{
                        showBrand: !!data.brand?.name,
                        brand: data.brand,
                        lastOrdered: data.lastOrdered,
                        showLastOrdered: true,
                    }}
                />
            </DataTableCell>

            <DataTableCell {...styles.dataCell}>{parseInt(data.lastOrderedQty, 10) || EMPTY_VALUE}</DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <ProductPrice showLabel={false} product={productContext} extendedStyles={styles.productPrice} />
                {settingsCollection.productSettings.showInventoryAvailability ? (
                    <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                ) : null}
            </DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <StyledWrapper {...styles.actionsWrapper}>
                    {productContext.product.canAddToCart && (
                        <StyledWrapper {...styles.addToCartSection}>
                            <ProductActions iconOnly={true} extendedStyles={styles.productActionStyles} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.compareWrapper}>
                        <ProductListProductImage showImage={false} showCompare={true} />
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </DataTableCell>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadSummaryCard);
